import { z } from 'zod';
import { CreateKpiSchema, type KpiDto } from './controls';

/**
 * Le viste personali (ADR 0194 § 2): una pagina di riquadri, ognuno una domanda dichiarata sui dati, mai SQL libero.
 * La domanda è quella degli indicatori (ADR 0213): tipo, misura, filtri, periodo, raggruppamento, rapporto, anno prima,
 * grafico. Si salva con nome, proprietario e versioni; il titolare la condivide con uno o più ruoli.
 */
export const ViewTileSchema = z.object({
  id: z.string().trim().min(1).max(40).regex(/^[a-z0-9_-]+$/),
  titolo: z.string().trim().min(2).max(120),
  /** Larghezza nella pagina: un riquadro (1) o due (2). */
  larghezza: z.union([z.literal(1), z.literal(2)]).default(1),
  domanda: CreateKpiSchema.omit({ name: true }),
});
export type ViewTile = z.infer<typeof ViewTileSchema>;

export const SaveViewSchema = z
  .object({
    nome: z.string().trim().min(2).max(120),
    riquadri: z.array(ViewTileSchema).max(24),
  })
  .refine((v) => new Set(v.riquadri.map((r) => r.id)).size === v.riquadri.length, { message: 'Due riquadri con lo stesso id' });
export type SaveViewInput = z.infer<typeof SaveViewSchema>;

/** Con chi la condivide il titolare: i ruoli dell'azienda (chiavi); vuoto = solo sua. */
export const ShareViewSchema = z.object({ ruoli: z.array(z.string().trim().min(1).max(200)).max(100) });
export type ShareViewInput = z.infer<typeof ShareViewSchema>;

export interface ViewSummaryDto {
  id: string;
  nome: string;
  versione: number;
  /** La persona che guarda è il proprietario: solo lui la cambia; gli altri la duplicano. */
  mia: boolean;
  proprietario: string;
  ruoli: string[];
  riquadri: number;
  aggiornata_il: string;
}

export interface ViewTileDto extends ViewTile {
  /** Il risultato calcolato all'apertura con i permessi di chi guarda; null se il riquadro è chiuso. */
  risultato: KpiDto | null;
  /** Perché il riquadro è chiuso per chi guarda (un campo o un tipo che non può vedere). */
  chiuso: string | null;
}

export interface ViewDto extends Omit<ViewSummaryDto, 'riquadri'> {
  riquadri: ViewTileDto[];
}

export interface ViewVersionDto {
  versione: number;
  nome: string;
  riquadri: number;
  salvata_da: string;
  salvata_il: string;
}
